import {
  buildProductTokenRef as buildProductTokenRefCore,
  buildOrderedPairMarketRefs,
  formatLegacyPoolId,
  formatPairLabel,
  formatPoolId,
  getTokenLegRole,
  isNativeLeg,
  isNativeLegAddress,
  isNativeTokenAddress,
  isWrappedNativeAddress,
  NATIVE_LEG,
  NATIVE_TOKEN_ADDRESS,
  orderPairLegsForDisplay,
  toCatalogAddress,
  toDisplaySymbol,
  toOnChainAddress as toOnChainAddressCore,
} from '@sirius-cybernetics-42/product-config'

import type { ChainId } from '@/sdk'

import type { MarketTokenRef, PairSummary, PoolSummary } from './types'

export {
  formatLegacyPoolId,
  formatPairLabel,
  formatPoolId,
  getTokenLegRole,
  isNativeLeg,
  isNativeLegAddress,
  isNativeTokenAddress,
  isWrappedNativeAddress,
  NATIVE_LEG,
  NATIVE_TOKEN_ADDRESS,
  orderPairLegsForDisplay,
  toCatalogAddress,
  toDisplaySymbol,
}

export function toOnChainAddress(address: string, chainId?: ChainId): string {
  void chainId
  return toOnChainAddressCore(address)
}

export function addressesEquivalent(a: string, b: string, chainId?: ChainId): boolean {
  void chainId
  return toCatalogAddress(a) === toCatalogAddress(b)
}

export function buildDisplayTokenRef(
  address: string,
  symbol: string,
  chainId: ChainId,
  name = symbol,
): MarketTokenRef {
  const ref = buildProductTokenRefCore(address, symbol, name, chainId)
  return {
    address: ref.address,
    symbol: ref.symbol,
    role: ref.role,
  }
}

export function normalizePoolSummary(pool: PoolSummary, chainId: ChainId): PoolSummary {
  const token0 = buildDisplayTokenRef(pool.token0.address, pool.token0.symbol, chainId)
  const token1 = buildDisplayTokenRef(pool.token1.address, pool.token1.symbol, chainId)
  const ordered =
    pool.baseToken && pool.quoteToken
      ? {
          base: buildDisplayTokenRef(pool.baseToken.address, pool.baseToken.symbol, chainId),
          quote: buildDisplayTokenRef(pool.quoteToken.address, pool.quoteToken.symbol, chainId),
        }
      : buildOrderedPairMarketRefs(
          pool.token0.address,
          pool.token0.symbol,
          pool.token0.symbol,
          pool.token1.address,
          pool.token1.symbol,
          pool.token1.symbol,
          chainId,
        )
  const displayId = formatPoolId(
    pool.token0.address,
    pool.token0.symbol,
    pool.token1.address,
    pool.token1.symbol,
    chainId,
  )
  const legacyId =
    pool.legacyId ??
    formatLegacyPoolId(
      pool.token0.address,
      pool.token0.symbol,
      pool.token1.address,
      pool.token1.symbol,
      chainId,
    )

  return {
    ...pool,
    id: displayId,
    legacyId,
    pair: formatPairLabel(
      pool.token0.address,
      pool.token0.symbol,
      pool.token1.address,
      pool.token1.symbol,
      chainId,
    ),
    token0,
    token1,
    baseToken: ordered.base,
    quoteToken: ordered.quote,
    reserves0: pool.reserves0.replace(pool.token0.symbol, token0.symbol),
    reserves1: pool.reserves1.replace(pool.token1.symbol, token1.symbol),
    lpTokenSymbol: pool.lpTokenSymbol
      ? pool.lpTokenSymbol
          .replace(pool.token0.symbol, token0.symbol)
          .replace(pool.token1.symbol, token1.symbol)
      : pool.lpTokenSymbol,
  }
}

export function normalizePairSummary(pair: PairSummary, chainId: ChainId): PairSummary {
  const ordered =
    pair.baseToken && pair.quoteToken
      ? {
          base: buildDisplayTokenRef(pair.baseToken.address, pair.baseToken.symbol, chainId),
          quote: buildDisplayTokenRef(pair.quoteToken.address, pair.quoteToken.symbol, chainId),
        }
      : buildOrderedPairMarketRefs(
          pair.token0Address,
          pair.token0Symbol,
          pair.token0Symbol,
          pair.token1Address,
          pair.token1Symbol,
          pair.token1Symbol,
          chainId,
        )

  return {
    ...pair,
    token0Address: toCatalogAddress(pair.token0Address),
    token1Address: toCatalogAddress(pair.token1Address),
    pair: formatPairLabel(
      pair.token0Address,
      pair.token0Symbol,
      pair.token1Address,
      pair.token1Symbol,
      chainId,
    ),
    token0Symbol: toDisplaySymbol(pair.token0Address, pair.token0Symbol, chainId),
    token1Symbol: toDisplaySymbol(pair.token1Address, pair.token1Symbol, chainId),
    baseToken: ordered.base,
    quoteToken: ordered.quote,
  }
}

export function resolvePoolIdAlias<T extends { id: string; legacyId?: string }>(
  poolId: string,
  pools: readonly T[],
): T | undefined {
  const key = poolId.toLowerCase()
  const byId = pools.find((pool) => pool.id.toLowerCase() === key)
  if (byId) return byId
  return pools.find((pool) => pool.legacyId?.toLowerCase() === key)
}

/** @deprecated Usar `isNativeLegAddress`. */
export function isWrappedNativeCatalogAddress(address: string): boolean {
  return isNativeLegAddress(address) && !isNativeTokenAddress(address)
}
